import Nav from "@/components/Nav";
import Ribbon from "@/components/Ribbon";
import BookingForm from "@/components/BookingForm";
import ReviewDialog from "@/components/ReviewDialog";
import FloatingActions from "@/components/FloatingActions";
import Gallery from "@/components/Gallery";
import GoogleReviewsBadge from "@/components/GoogleReviewsBadge";
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Star, Instagram, MapPin } from "lucide-react";
import { useI18n, MEDIA, BRAND } from "@/i18n";
import { ChevronDown } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

function Hero() {
  const { t } = useI18n();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  return (
    <section className="relative min-h-screen flex items-end overflow-hidden" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${MEDIA.hero})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#14110D]/95 via-[#14110D]/55 to-[#14110D]/15" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#14110D]/50 to-transparent" />
      <div className="absolute inset-0 grain" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full pb-24 pt-40">
        <p className="overline text-[#FAF9F6]/90 reveal">{t.hero.overline}</p>
        <div className="gold-divider mt-4 reveal" />
        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.98] text-[#FAF9F6] mt-6 max-w-5xl whitespace-pre-line reveal delay-1">
          {t.hero.title}
        </h1>
        <p className="mt-8 text-[#FAF9F6]/85 text-lg lg:text-xl max-w-2xl font-light leading-relaxed reveal delay-2">
          {t.hero.sub}
        </p>
        <div className="mt-12 flex flex-wrap gap-4 reveal delay-3">
          <button
            onClick={() => scrollTo("booking")}
            data-testid="hero-cta-primary"
            className="btn-terracotta px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs"
          >
            {t.hero.cta}
          </button>
          <button
            onClick={() => scrollTo("classes")}
            data-testid="hero-cta-secondary"
            className="px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs border border-[#FAF9F6]/60 text-[#FAF9F6] hover:bg-[#FAF9F6] hover:text-[#2C2520] transition"
          >
            {t.hero.ctaGhost}
          </button>
        </div>

        <button
          onClick={() => scrollTo("classes")}
          className="absolute right-6 bottom-10 hidden md:flex flex-col items-center text-[#FAF9F6]/70 hover:text-[#FAF9F6] gap-2"
          aria-label="scroll"
        >
          <span className="text-[10px] tracking-[0.3em] uppercase rotate-90 origin-center mt-10">scroll</span>
          <ChevronDown className="w-5 h-5 animate-bounce" />
        </button>
      </div>
    </section>
  );
}

function Classes() {
  const { t, lang } = useI18n();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const packages = [
    { key: "coppie", accent: "warm" },
    { key: "gruppo", accent: "warm" },
    { key: "evento", accent: "warm", contact: true },
  ];

  // WhatsApp greeting per lingua per il pacchetto Evento privato / aziendale
  const eventoGreetings = {
    it: "Ciao Alessandro, vorrei informazioni per un evento privato o aziendale sul terrazzo.",
    en: "Hi Alessandro, I'd like info about a private / corporate event on the rooftop.",
    fr: "Bonjour Alessandro, je souhaite des informations pour un événement privé ou corporate sur la terrasse.",
    es: "Hola Alessandro, me gustaría información sobre un evento privado o corporativo en la terraza.",
    de: "Hallo Alessandro, ich hätte gerne Informationen zu einem privaten oder Firmenevent auf der Terrasse.",
    ja: "アレッサンドロさん、テラスでのプライベート/法人イベントについて教えてください。",
  };
  const eventoWaLink = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(eventoGreetings[lang] || eventoGreetings.it)}`;

  return (
    <section id="classes" className="py-24 sm:py-32 bg-[#FAF9F6]" data-testid="classes-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-6 md:gap-10 mb-12 md:mb-16">
          <div className="md:col-span-4">
            <p className="overline">{t.classes.overline}</p>
          </div>
          <div className="md:col-span-8">
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.05] text-[#2C2520] whitespace-pre-line">
              {t.classes.title}
            </h2>
          </div>
        </div>

        {/* Sunset banner */}
        <div className="relative overflow-hidden mb-8 md:mb-10 rounded-sm">
          <img
            src={MEDIA.sunset}
            alt="Rome sunset from the rooftop"
            className="w-full aspect-[21/9] md:aspect-[21/7] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1F1A14]/50 via-transparent to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10">
            <p className="overline text-[#FAF9F6]">{t.classes.terraceTitle}</p>
            <p className="font-serif-display text-xl sm:text-2xl md:text-3xl mt-2 text-[#FAF9F6] leading-tight max-w-2xl">
              {t.classes.terraceSub}
            </p>
          </div>
        </div>

        {/* Package cards - text only */}
        <div className="grid md:grid-cols-2 gap-4 md:gap-6">
          {packages.map((p) => {
            const pkg = t.classes[p.key];
            const isDark = p.accent === "dark";
            return (
              <article
                key={p.key}
                data-testid={`class-card-${p.key}`}
                className={`p-8 md:p-10 flex flex-col justify-between hover-lift transition-colors ${
                  isDark
                    ? "bg-[#1F1A14] text-[#FAF9F6] border border-[#2C2520]"
                    : "bg-[#F2EFEB] border border-[#E5E0D8] text-[#1F1A14]"
                }`}
              >
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <p className={`overline ${isDark ? "text-[#D4B98D]" : ""}`}>{pkg.persons}</p>
                    {pkg.badge && (
                      <span className="text-[10px] tracking-widest uppercase px-2.5 py-1 bg-[#C85A3C] text-[#FAF9F6] rounded-sm">
                        {pkg.badge}
                      </span>
                    )}
                  </div>
                  <h3 className={`font-serif-display text-3xl md:text-4xl mt-3 leading-[1.1] ${isDark ? "text-[#FAF9F6]" : "text-[#1F1A14]"}`}>
                    {pkg.title}
                  </h3>
                  <p className={`mt-5 leading-relaxed text-base md:text-lg ${isDark ? "text-[#FAF9F6]/75" : "text-[#5C564F]"}`}>
                    {pkg.desc}
                  </p>
                </div>
                <div className="mt-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <span className={`font-serif-display text-2xl sm:text-3xl tracking-wide ${isDark ? "text-[#D4B98D]" : "text-[#B89668]"}`}>
                    {pkg.price}
                  </span>
                  {p.contact ? (
                    <a
                      href={eventoWaLink}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`class-cta-${p.key}`}
                      className={`px-6 py-3 rounded-sm tracking-widest uppercase text-xs whitespace-nowrap transition inline-flex items-center gap-2 ${
                        isDark
                          ? "border border-[#FAF9F6]/60 hover:bg-[#FAF9F6] hover:text-[#1F1A14]"
                          : "btn-ghost-dark"
                      }`}
                    >
                      {pkg.cta}
                    </a>
                  ) : (
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <Link
                        to={`/experiences/${p.key}`}
                        data-testid={`class-details-${p.key}`}
                        className={`px-5 py-3 rounded-sm tracking-widest uppercase text-xs whitespace-nowrap transition text-center ${
                          isDark
                            ? "text-[#D4B98D] hover:text-[#FAF9F6]"
                            : "text-[#B89668] hover:text-[#1F1A14]"
                        }`}
                      >
                        {t.classes.detailsCta}
                      </Link>
                      <button
                        onClick={() => scrollTo("booking")}
                        data-testid={`class-cta-${p.key}`}
                        className={`px-6 py-3 rounded-sm tracking-widest uppercase text-xs whitespace-nowrap transition ${
                          isDark
                            ? "border border-[#FAF9F6]/60 hover:bg-[#FAF9F6] hover:text-[#1F1A14]"
                            : "btn-ghost-dark"
                        }`}
                      >
                        {pkg.cta}
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </div>

        {/* Final note */}
        <p className="mt-10 md:mt-12 max-w-3xl mx-auto text-center text-[#5C564F] italic text-base md:text-lg leading-relaxed">
          {t.classes.note}
        </p>
      </div>
    </section>
  );
}

function BuildEveningCallout() {
  const { t } = useI18n();
  const bye = t.classes.buildEvening;
  return (
    <section className="bg-[#1F1A14] text-[#FAF9F6] relative overflow-hidden" data-testid="bye-callout">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-25"
        style={{ backgroundImage: `url(${MEDIA.terrace})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#14110D] via-[#14110D]/85 to-transparent" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-20 sm:py-24 grid md:grid-cols-12 gap-10 items-center">
        <div className="md:col-span-7">
          <p className="overline text-[#D4B98D]">{bye.heroOverline}</p>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-tight max-w-2xl">
            {bye.heroTitle}
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#FAF9F6]/80 max-w-2xl leading-relaxed">
            {bye.heroSub}
          </p>
          <Link
            to="/build-your-evening"
            data-testid="bye-callout-cta"
            className="mt-7 inline-flex items-center gap-2 px-7 py-3.5 bg-[#C85A3C] text-white text-sm tracking-widest uppercase hover:bg-[#B24A2E] transition rounded-sm"
          >
            {bye.navLink} <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function About() {
  const { t } = useI18n();
  return (
    <section id="about" className="py-24 sm:py-32 bg-[#F2EFEB] paper-texture" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden ring-1 ring-[#B89668]/30 ring-offset-4 ring-offset-[#F2EFEB]">
            <img src={MEDIA.host} alt="Alessandro Pivano" className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-7 md:pl-8">
          <p className="overline">{t.about.overline}</p>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[#1F1A14] leading-[1.05] whitespace-pre-line">
            {t.about.title}
          </h2>
          <div className="gold-divider mt-6" />
          <p className="mt-8 text-[#5C564F] text-lg leading-relaxed whitespace-pre-line max-w-xl">
            {t.about.body}
          </p>
          <p className="mt-8 font-serif-display text-3xl italic text-[#B89668]">{t.about.signature}</p>
        </div>
      </div>
    </section>
  );
}

function Experience() {
  const { t } = useI18n();
  return (
    <section id="experience" className="py-24 sm:py-32 bg-[#FAF9F6] paper-texture" data-testid="experience-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <div className="grid md:grid-cols-12 gap-10 mb-16">
          <div className="md:col-span-5">
            <p className="overline">{t.experience.overline}</p>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[#2C2520] leading-[1.05] whitespace-pre-line">
              {t.experience.title}
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-4 gap-0 border-t border-[#E5E0D8]">
          {t.experience.items.map((item, i) => (
            <div
              key={i}
              className={`p-8 md:p-10 border-b border-[#E5E0D8] ${
                i < 3 ? "md:border-r md:border-b-0" : ""
              }`}
            >
              <p className="font-serif-display text-5xl text-[#B89668]">{item.tag}</p>
              <h3 className="font-serif-display text-2xl mt-6 text-[#1F1A14]">{item.title}</h3>
              <p className="mt-4 text-[#5C564F] leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>

        {/* SEO copy — local intent for search engines */}
        <div className="mt-16 md:mt-20 max-w-4xl">
          <p className="text-lg md:text-xl text-[#3F3A34] leading-[1.7]" data-testid="experience-seo">
            {t.experience.seo}
          </p>
        </div>
      </div>
    </section>
  );
}

function Testimonials() {
  const { t } = useI18n();
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    axios
      .get(`${API}/reviews?limit=6`)
      .then((r) => setReviews(r.data || []))
      .catch(() => setReviews([]))
      .finally(() => setLoaded(true));
  }, [reloadKey]);

  const items = reviews.map((r) => ({ id: r.id, quote: r.quote, author: r.name, origin: r.origin, rating: r.rating }));
  const isEmpty = loaded && items.length === 0;
  const gridCols = items.length === 1
    ? "max-w-2xl mx-auto"
    : items.length === 2
    ? "md:grid-cols-2 max-w-5xl mx-auto"
    : "md:grid-cols-3";

  const reviewTrigger = (label) => (
    <ReviewDialog
      onSubmitted={() => setReloadKey((k) => k + 1)}
      trigger={
        <button
          data-testid="open-review-dialog"
          className="btn-terracotta px-8 py-4 rounded-sm tracking-[0.2em] uppercase text-xs"
        >
          {label}
        </button>
      }
    />
  );

  return (
    <section id="testimonials" className="py-24 sm:py-32 bg-[#F2EFEB] relative" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
        <p className="overline">{t.testimonials.overline}</p>

        {isEmpty ? (
          // ---------- ELEGANT EMPTY STATE ----------
          <div className="mt-12 grid md:grid-cols-12 gap-10 items-center" data-testid="testimonials-empty">
            <div className="md:col-span-7">
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#2C2520] leading-[1.05] whitespace-pre-line">
                {t.testimonials.emptyTitle}
              </h2>
              <p className="mt-6 text-[#5C564F] text-base sm:text-lg leading-relaxed max-w-xl">
                {t.testimonials.emptySub}
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
                {reviewTrigger(t.testimonials.emptyCta)}
                <a
                  href={BRAND.googleProfile}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="google-review-cta"
                  className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-[#5C564F] hover:text-[#C85A3C] transition border-b border-[#5C564F]/30 hover:border-[#C85A3C] pb-1"
                >
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                  {t.testimonials.orGoogle}
                </a>
              </div>
            </div>
            <div className="md:col-span-5">
              <div className="aspect-[4/5] bg-[#FAF9F6] border border-[#E5E0D8] flex items-center justify-center relative overflow-hidden">
                <span className="absolute -top-8 left-6 font-serif-display text-[12rem] text-[#C85A3C]/15 leading-none select-none">"</span>
                <div className="text-center px-8 relative z-10">
                  <div className="flex justify-center gap-1 mb-6">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className="w-5 h-5 text-[#E5E0D8]" strokeWidth={1.5} />
                    ))}
                  </div>
                  <p className="font-serif-display text-2xl text-[#2C2520]/40 italic leading-snug">
                    {t.testimonials.shareTitle}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ) : (
          // ---------- REAL REVIEWS ----------
          <>
            <div className={`grid gap-8 mt-10 ${gridCols}`}>
              {items.slice(0, 6).map((tItem, i) => (
                <div key={tItem.id} className="bg-[#FAF9F6] border border-[#E5E0D8] p-8 flex flex-col hover-lift" data-testid={`testimonial-card-${i}`}>
                  <div className="flex gap-0.5 mb-3">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${n <= tItem.rating ? "fill-[#C85A3C] text-[#C85A3C]" : "text-[#E5E0D8]"}`}
                        strokeWidth={1.5}
                      />
                    ))}
                  </div>
                  <span className="font-serif-display text-6xl text-[#C85A3C] leading-none">"</span>
                  <p className="font-serif-display text-2xl leading-snug text-[#2C2520] mt-2">{tItem.quote}</p>
                  <div className="mt-auto pt-8">
                    <p className="text-sm font-semibold text-[#2C2520]">{tItem.author}</p>
                    <p className="text-xs tracking-widest uppercase text-[#5C564F] mt-1">{tItem.origin}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-16 border-t border-[#E5E0D8] pt-12 grid md:grid-cols-12 gap-8 items-center">
              <div className="md:col-span-8">
                <p className="overline">{t.testimonials.shareSection}</p>
                <h3 className="font-serif-display text-3xl md:text-4xl mt-3 text-[#2C2520] leading-tight">
                  {t.testimonials.shareTitle}
                </h3>
                <p className="mt-3 text-[#5C564F] max-w-xl">{t.testimonials.shareSub}</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                {reviewTrigger(t.testimonials.shareCta)}
                <a
                  href={BRAND.googleProfile}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="google-review-cta"
                  className="inline-flex items-center gap-2 mt-4 md:mt-3 text-xs tracking-widest uppercase text-[#5C564F] hover:text-[#C85A3C] transition"
                >
                  <MapPin className="w-3.5 h-3.5" strokeWidth={2} />
                  {t.testimonials.orGoogle}
                </a>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function FAQ() {
  const { t } = useI18n();
  return (
    <section id="faq" className="py-24 sm:py-32 bg-[#FAF9F6]" data-testid="faq-section">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
        <p className="overline">{t.faq.overline}</p>
        <h2 className="font-serif-display text-5xl md:text-6xl mt-4 text-[#2C2520]">{t.faq.title}</h2>

        <Accordion type="single" collapsible className="mt-12">
          {t.faq.items.map((it, i) => (
            <AccordionItem key={i} value={`i-${i}`} className="border-b border-[#E5E0D8]">
              <AccordionTrigger className="font-serif-display text-2xl text-left text-[#2C2520] py-6 hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="text-[#5C564F] text-base leading-relaxed pb-6 max-w-3xl">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

function Footer() {
  const { t } = useI18n();
  return (
    <footer className="bg-[#14110D] text-[#FAF9F6]/80 pt-16 pb-8 relative grain" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid md:grid-cols-3 gap-10">
        <div>
          <p className="font-serif-display text-3xl text-[#FAF9F6]">{BRAND.name}</p>
          <p className="mt-3 text-sm text-[#FAF9F6]/60">{t.footer.tagline}</p>
        </div>
        <div>
          <p className="overline text-[#C85A3C]">{t.footer.contacts}</p>
          <a href={`mailto:${BRAND.email}`} className="block mt-4 text-sm hover:text-[#C85A3C] transition" data-testid="footer-email">{BRAND.email}</a>
          <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noreferrer" className="block text-sm hover:text-[#C85A3C] transition" data-testid="footer-whatsapp">WhatsApp · {BRAND.phone}</a>
          <p className="text-sm mt-2">{BRAND.zone}</p>
          <div className="flex items-center gap-3 mt-5">
            <a
              href={BRAND.instagram}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-instagram"
              aria-label="Instagram"
              className="w-9 h-9 border border-[#FAF9F6]/25 flex items-center justify-center hover:border-[#C85A3C] hover:text-[#C85A3C] transition rounded-sm"
            >
              <Instagram className="w-4 h-4" strokeWidth={1.6} />
            </a>
            <a
              href={BRAND.googleProfile}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-google"
              aria-label="Google Maps"
              className="w-9 h-9 border border-[#FAF9F6]/25 flex items-center justify-center hover:border-[#C85A3C] hover:text-[#C85A3C] transition rounded-sm"
            >
              <MapPin className="w-4 h-4" strokeWidth={1.6} />
            </a>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 mt-12 pt-6 border-t border-[#FAF9F6]/10 text-xs text-[#FAF9F6]/40 flex flex-col sm:flex-row justify-between gap-3">
        <span>© {new Date().getFullYear()} {BRAND.name} · Alessandro Pivano</span>
        <div className="flex items-center gap-4">
          <Link
            to="/cookie-policy"
            className="hover:text-[#C85A3C] transition"
            data-testid="footer-cookie-policy"
          >
            {t.footer.cookiePolicy}
          </Link>
          <button
            type="button"
            onClick={() => window.dispatchEvent(new CustomEvent("ecv:openCookieSettings"))}
            className="hover:text-[#C85A3C] transition"
            data-testid="footer-cookie-settings"
          >
            {t.footer.cookieSettings}
          </button>
          <span>{t.footer.rights}</span>
        </div>
      </div>
    </footer>
  );
}

export default function Landing() {
  return (
    <div className="bg-[#FAF9F6] min-h-screen">
      <Nav />
      <Hero />
      <Ribbon />
      <BuildEveningCallout />
      <Classes />
      <About />
      <Experience />
      <Gallery />
      <GoogleReviewsBadge />
      <Testimonials />
      <BookingForm />
      <FAQ />
      <Footer />
      <FloatingActions />
    </div>
  );
}
